// Where the assistant lives (ERP-129): a button in the top bar that opens it beside the page, so
// it can talk about what you are looking at, and a page of its own for longer work.
//
// The drawer is as wide as you drag it, or the whole window (ERP-132); the width is remembered in
// this browser. Anything on a page can open it with a question ready: `askAssistant("…")`.
import { t } from "@keel/core";
import { Icon, useCrumbs } from "@keel/ui";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Chat, CloseAssistant } from "./Chat";

const ASK = "keel:assistant";
const WIDTH = "keel-assistant-width";
const MIN = 380;

/** Open the assistant with this question in its box, for the person to send or change. */
export function askAssistant(question: string) {
  window.dispatchEvent(new CustomEvent(ASK, { detail: question }));
}

const stored = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const store = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
};

export function AssistantButton() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ text: string; n: number } | null>(null);
  useEffect(() => {
    // ⌘J / Ctrl+J opens and closes it from anywhere.
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const ask = (e: Event) => {
      const text = String((e as CustomEvent).detail ?? "");
      setPrefill((p) => ({ text, n: (p?.n ?? 0) + 1 }));
      setOpen(true);
    };
    window.addEventListener("keydown", key);
    window.addEventListener(ASK, ask);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener(ASK, ask);
    };
  }, []);
  return (
    <>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setOpen(true)}
        aria-label={t("Assistant")}
        title={`${t("Ask the assistant")} (⌘J)`}
        data-testid="assistant-open"
      >
        <Icon name="sparkle" />
      </button>
      {/* On the body: the top bar would otherwise hold the drawer inside itself. */}
      {open &&
        createPortal(<AssistantDrawer onClose={() => setOpen(false)} prefill={prefill} />, document.body)}
    </>
  );
}

function AssistantDrawer({
  onClose,
  prefill,
}: {
  onClose: () => void;
  prefill: { text: string; n: number } | null;
}) {
  const [width, setWidth] = useState(() => Math.max(MIN, Number(stored(WIDTH)) || 560));
  const [full, setFull] = useState(() => stored(`${WIDTH}-full`) === "1");
  const dragging = useRef(false);
  useEffect(() => {
    const esc = (e: KeyboardEvent) =>
      e.key === "Escape" && !document.querySelector("dialog[open]") && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // Drag the left edge: the drawer is as wide as the distance to the window's right side.
  const startDrag = (e: ReactPointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    let last = width;
    const move = (m: PointerEvent) => {
      if (!dragging.current) return;
      last = Math.min(window.innerWidth - 48, Math.max(MIN, window.innerWidth - m.clientX));
      setWidth(last);
    };
    const up = () => {
      dragging.current = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("assistant-resizing");
      store(WIDTH, String(Math.round(last)));
    };
    document.body.classList.add("assistant-resizing");
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const toggleFull = () => {
    store(`${WIDTH}-full`, full ? "0" : "1");
    setFull(!full);
  };

  return (
    <>
      <button type="button" className="drawer-scrim" aria-label={t("Close")} onClick={onClose} />
      <aside
        className={`drawer assistant-drawer ${full ? "full" : ""}`}
        style={full ? undefined : { width: `min(${width}px, 100vw)` }}
        aria-label={t("Assistant")}
      >
        {!full && (
          // biome-ignore lint/a11y/noStaticElementInteractions: a drag handle; the expand button is the keyboard way
          <div
            className="assistant-resize"
            onPointerDown={startDrag}
            onDoubleClick={() => {
              setWidth(560);
              store(WIDTH, "560");
            }}
            title={t("Drag to resize; double-click for the usual width")}
          />
        )}
        <div className="drawer-head">
          <div>
            <h2>{t("Assistant")}</h2>
            <p className="sub muted" style={{ textTransform: "none" }}>
              {t("Works with your rights; asks before it changes anything.")}
            </p>
          </div>
          <div className="row" style={{ gap: 4 }}>
            <button
              type="button"
              className="icon-btn assistant-expand"
              onClick={toggleFull}
              aria-label={full ? t("Back to the side") : t("Full width")}
              title={full ? t("Back to the side") : t("Full width")}
            >
              <Icon name={full ? "shrink" : "expand"} />
            </button>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t("Close")}>
              <Icon name="x" />
            </button>
          </div>
        </div>
        <CloseAssistant.Provider value={onClose}>
          <Chat prefill={prefill} />
        </CloseAssistant.Provider>
      </aside>
    </>
  );
}

export function AssistantPage() {
  useCrumbs([{ label: t("Assistant") }]);
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t("Assistant")}</h1>
          <p className="muted">
            {t(
              "Ask how something works, ask about your data, or have it do things for you. It asks before it changes anything.",
            )}
          </p>
        </div>
      </div>
      <section className="card assistant-card">
        <Chat full />
      </section>
    </div>
  );
}

/** On the customer or supplier card: open the assistant with a question about them (ERP-135). */
export function AskAboutParty({ party }: { party: { name: string } }) {
  return (
    <button
      type="button"
      className="btn"
      onClick={() =>
        askAssistant(
          t("Tell me about {name}: what they owe, what they bought, and anything I should know.", {
            name: party.name,
          }),
        )
      }
      title={t("Open the assistant with a question about them")}
    >
      <Icon name="sparkle" size={14} />
      {t("Ask the assistant")}
    </button>
  );
}
